import { useEffect, useMemo, useRef, useState } from 'react';
import { searchSkins, findSkinImage, CONDITION_MAP } from './skinIndex';
import './ModalTheme.css';

/**
 * Wishlist editor - put a skin on the watchboard without owning it.
 *
 * Until this existed the only door onto the wishlist was the heart button on a
 * holding, so the board could only watch things already in the armory. The
 * point of a watchlist is the opposite: the skins you are waiting to buy. This
 * modal is that door, and the same one edits or removes a wish afterwards.
 *
 * The picker searches the skins database rather than the tracker rows for the
 * same reason - a wish is about an item you do not have yet.
 *
 * Props:
 *   initial              - the wish being edited, or null to add a new one
 *   wishlist             - what is already watched, for the duplicate warning
 *   skinsData            - the skins database, searched for names and pictures
 *   itemsWithConditions  - base names that have wear tiers (from SkinTable)
 *   livePrices           - Buff prices, so a target can be set against the ask
 *   variant              - 'zone' | 'classic' skin, see ModalTheme.css
 *   onSave               - (item) => void, the whole wish object
 *   onDelete             - (id) => void, shown only when editing
 *   onClose              - () => void
 */

// Two characters before the search runs: one letter matches thousands of
// things and none of them is what was meant.
const MIN_QUERY = 2;

// The picker spelling, which is what getWishPrice and archiveName map from,
// beside the two-letter name the market actually uses. Short labels keep the
// six tiers on one row, where they read as the scale they are.
const CONDITIONS = [
  { value: null,             short: 'Any', label: 'Any condition' },
  { value: 'Factory New',    short: 'FN',  label: 'Factory New' },
  { value: 'Minimal Wear',   short: 'MW',  label: 'Minimal Wear' },
  { value: 'Field Tested',   short: 'FT',  label: 'Field-Tested' },
  { value: 'Well Worn',      short: 'WW',  label: 'Well-Worn' },
  { value: 'Battle Scarred', short: 'BS',  label: 'Battle-Scarred' },
];

// Which wear to guess at when an item has tiers, most-traded first.
const WEAR_PREFERENCE = ['Field Tested', 'Minimal Wear', 'Factory New', 'Well Worn', 'Battle Scarred'];

// Quick targets, as a discount off what it is asking today.
const CUTS = [5, 10, 15, 25];

function priceOf(livePrices, skin, condition) {
  if (!livePrices || !skin) return null;
  if (condition) {
    const cond = CONDITION_MAP[condition] || condition;
    return (livePrices[`${skin} (${cond})`] || livePrices[skin])?.starting_at?.price ?? null;
  }
  return livePrices[skin]?.starting_at?.price ?? null;
}

export default function WishlistAddModal({
  initial = null,
  wishlist = [],
  skinsData = [],
  itemsWithConditions,
  livePrices,
  variant = 'classic',
  onSave,
  onDelete,
  onClose,
}) {
  const editing = !!initial;

  const [skin, setSkin]           = useState(initial?.skin || '');
  const [condition, setCondition] = useState(initial?.condition || null);
  const [target, setTarget]       = useState(
    initial?.targetPrice != null ? String(initial.targetPrice) : '');
  const [note, setNote]           = useState(initial?.note || '');
  const [query, setQuery]         = useState('');

  const searchRef = useRef(null);
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { if (!editing) searchRef.current?.focus(); }, [editing]);

  const q = query.trim();
  const hits = useMemo(
    () => (q.length >= MIN_QUERY ? searchSkins(skinsData, q, 24) : []),
    [skinsData, q]);

  // An unloaded database is an empty set, which would hide the wear tiers on
  // every item. Only trust it once it has something in it.
  const hasTiers = !skin || !itemsWithConditions?.size || itemsWithConditions.has(skin);

  useEffect(() => { if (!hasTiers && condition) setCondition(null); }, [hasTiers, condition]);

  const image = useMemo(
    () => (skin ? findSkinImage(skinsData, skin, condition) : null),
    [skinsData, skin, condition]);

  const now = priceOf(livePrices, skin, condition);
  const targetNum = target.trim() === '' ? null : Number(target);
  const targetOk  = targetNum == null || (Number.isFinite(targetNum) && targetNum > 0);
  const gapPct    = now != null && targetNum ? (now - targetNum) / targetNum * 100 : null;

  const duplicate = useMemo(() => (wishlist || []).find(
    w => w.id !== initial?.id && w.skin === skin && (w.condition || null) === (condition || null)),
    [wishlist, initial, skin, condition]);

  const ready = !!skin && targetOk && !duplicate;

  const pick = (name) => {
    setSkin(name);
    setQuery('');
    // A wear is picked for tiered items rather than left on "Any condition",
    // because prices are quoted per tier: a bare "AK-47 | Redline" usually has
    // no live price at all, and a wish with no price can never hit its target.
    // Field-Tested leads the preference list as the tier most things trade in;
    // the rest are there for the items that skip it. The picker below shows
    // every tier's price, so changing the guess is one click.
    const tiered = !itemsWithConditions?.size || itemsWithConditions.has(name);
    if (!tiered || priceOf(livePrices, name, null) != null) { setCondition(null); return; }
    setCondition(WEAR_PREFERENCE.find(c => priceOf(livePrices, name, c) != null) ?? null);
  };

  const save = () => {
    if (!ready) return;
    onSave?.({
      // Editing keeps the identity and the trail: a wish that changes its
      // target is the same wish, and the prices recorded against it are still
      // its own.
      id:           initial?.id || crypto.randomUUID(),
      skin,
      condition:    condition || null,
      targetPrice:  targetNum,
      note:         note.trim(),
      createdAt:    initial?.createdAt || new Date().toISOString(),
      priceHistory: initial?.priceHistory || [],
      ...(initial?.watchedFromHolding ? { watchedFromHolding: true } : {}),
    });
    onClose?.();
  };

  const remove = () => {
    if (!editing) return;
    onDelete?.(initial.id);
    onClose?.();
  };

  const title = editing ? 'Edit wish' : 'Add to wishlist';

  return (
    <div className={`mdl-overlay ${variant}`}
      onMouseDown={e => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={e => { if (e.target === e.currentTarget && pressedBackdrop.current) onClose?.(); }}>
      <div className="mdl-panel" role="dialog" aria-modal="true" aria-label={title}>

        <div className="mdl-head">
          <div className="mdl-head-txt">
            <div className="mdl-title">{title}</div>
            <div className="mdl-sub">
              Anything can go on the board, owned or not. Its price gets written down once
              a day, and a target turns it into an alert.
            </div>
          </div>
          <button className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mdl-body">
          <div className="mdl-stack gap">

            {/* ── The item ──────────────────────────────────── */}
            <div className="mdl-card">
              <div className="mdl-card-title">Item</div>

              {skin ? (
                <div className="wam-chosen">
                  {image
                    ? <img src={image} alt="" />
                    : <span className="wam-chosen-blank" />}
                  <div className="wam-chosen-txt">
                    <div className="wam-chosen-name" title={skin}>{skin}</div>
                    <div className="wam-chosen-meta">
                      {now != null
                        ? `asking $${now.toFixed(2)} on Buff today`
                        : 'no live price for this one yet'}
                    </div>
                  </div>
                  <button type="button" className="mdl-btn"
                    onClick={() => { setSkin(''); setCondition(null); }}>
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <input ref={searchRef} className="mdl-input wam-search" value={query}
                    placeholder="Search a skin, knife, sticker, case…"
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && hits.length) { e.preventDefault(); pick(hits[0].name); }
                    }} />

                  {q.length > 0 && q.length < MIN_QUERY && (
                    <div className="mdl-card-hint" style={{ marginTop: 10 }}>
                      Keep typing, two letters at least.
                    </div>
                  )}

                  {q.length >= MIN_QUERY && (
                    hits.length === 0 ? (
                      <div className="mdl-card-hint" style={{ marginTop: 10 }}>
                        Nothing in the skins database matches that.
                      </div>
                    ) : (
                      <div className="wam-hits">
                        {hits.map(it => {
                          // The bare name for the pictureless things, the
                          // middle wear for anything tiered: enough to tell two
                          // similar rows apart before either is chosen.
                          const p = priceOf(livePrices, it.name, null)
                                 ?? priceOf(livePrices, it.name, 'Field Tested');
                          return (
                            <button key={it.name} type="button" className="wam-hit"
                              onClick={() => pick(it.name)}>
                              <img src={it.image} alt="" loading="lazy" />
                              <span className="wam-hit-name" title={it.name}>{it.name}</span>
                              <span className="wam-hit-price">{p != null ? `$${p.toFixed(2)}` : 'n/a'}</span>
                            </button>
                          );
                        })}
                      </div>
                    )
                  )}
                </>
              )}
            </div>

            {/* ── Wear ──────────────────────────────────────── */}
            {skin && hasTiers && (
              <div className="mdl-card">
                <div className="mdl-card-title">Wear</div>
                <div className="mdl-card-hint">
                  Any watches the bare name, which is what a knife or a case wants. Pick a
                  wear and the target is measured against that tier alone.
                </div>
                <div className="wam-conds">
                  {CONDITIONS.map(c => {
                    const p = priceOf(livePrices, skin, c.value);
                    return (
                      <button key={c.short} type="button" title={c.label}
                        className={`wam-cond${condition === c.value ? ' on' : ''}`}
                        onClick={() => setCondition(c.value)}>
                        <span className="n">{c.short}</span>
                        <span className="p">{p != null ? `$${p.toFixed(2)}` : 'n/a'}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Prices are quoted per tier, so the bare name is often
                    unpriced. Said out loud here rather than left as a card that
                    never fills its meter. */}
                {condition == null && now == null && livePrices && (
                  <div className="mdl-card-hint" style={{ marginTop: 10 }}>
                    Nothing is quoted under the bare name, so this wish would have no price
                    to track. Pick the wear you are after.
                  </div>
                )}
              </div>
            )}

            {/* ── Target ────────────────────────────────────── */}
            <div className="mdl-card">
              <div className="mdl-card-title">Target price</div>
              <div className="mdl-card-hint">
                What you would happily pay. Leave it empty to only watch the price:
                no target means no alert, the card just tracks it.
              </div>
              <div className="mdl-controls">
                <span className="mdl-unit">$</span>
                <input className="mdl-input narrow mono" value={target} inputMode="decimal"
                  placeholder="0.00"
                  onChange={e => setTarget(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } }} />
                {now != null && CUTS.map(c => (
                  <button key={c} type="button" className="mdl-btn sm"
                    title={`${c}% under what it is asking today`}
                    onClick={() => setTarget((now * (1 - c / 100)).toFixed(2))}>
                    -{c}%
                  </button>
                ))}
              </div>
              {!targetOk && (
                <div className="mdl-card-hint" style={{ marginTop: 8 }}>
                  <strong>{target}</strong> is not a price. Numbers above zero only.
                </div>
              )}
              {targetOk && gapPct != null && (
                <div className="mdl-card-hint" style={{ marginTop: 8 }}>
                  {gapPct > 0
                    ? <>It is asking <strong>{gapPct.toFixed(1)}%</strong> above that today.
                        The meter fills as it comes down.</>
                    : <>Already at or under your target, so it lands on the board as a hit.</>}
                </div>
              )}
            </div>

            {/* ── Note ──────────────────────────────────────── */}
            <div className="mdl-card">
              <div className="mdl-card-title">Note</div>
              <div className="mdl-card-hint">
                Why you want it, or what you would trade for it. Shown on the card.
              </div>
              <textarea className="mdl-area wam-note" rows={2} value={note}
                placeholder="Blue gem pattern, or trade the AK for it…"
                onChange={e => setNote(e.target.value)} />
            </div>

            {duplicate && (
              <div className="mdl-card warn">
                <div className="mdl-card-title">Already on the board</div>
                <div className="mdl-card-hint">
                  <strong>{skin}</strong>{condition ? ` (${condition})` : ''} is already being
                  watched. Edit that card instead, or pick a different wear.
                </div>
              </div>
            )}

          </div>
        </div>

        <div className="mdl-foot">
          <span className="mdl-foot-note">
            {editing
              ? 'The price trail belongs to the wish, so changing the target keeps its history.'
              : 'Prices start being recorded from today, one reading a day.'}
          </span>
          {editing && <button className="mdl-btn danger" onClick={remove}>Remove</button>}
          <button className="mdl-btn" onClick={onClose}>Cancel</button>
          <button className="mdl-btn primary" onClick={save} disabled={!ready}>
            {editing ? 'Save' : 'Add to wishlist'}
          </button>
        </div>

      </div>
    </div>
  );
}
